import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ReportsApiError } from "../../../services/reportsApi";
import type {
  ConditionAnalysisBucketRow,
  ConditionAnalysisSituationRow,
  ConditionAnalysisSummaryRow,
  ConditionAnalysisScope,
  ConditionAnalysisSlice,
} from "../../../utils/conditionAnalysisSelectors";
import {
  buildBucketRowResolveBody,
  buildConditionSliceResolveBody,
  buildExplorerRowCohortResolveBody,
  buildSituationRowResolveBody,
  buildSummaryRowResolveBody,
  resolveAllCohortTradeIds,
  type CohortListFilter,
  type CohortTradesResolveRequestBody,
} from "../../../utils/cohortTradesResolve";
import type { ManagementExplorerSliceRow } from "../../../types/managementExplorer";
import { buildExplorerSliceId } from "../../../utils/managementExplorer";
import type { ReportPickSliceContext } from "../../../utils/reportPickListStorage";

export type MarketCohortPreviewStatus = "idle" | "loading" | "ready" | "error" | "empty";

export type MarketCohortPreviewState = {
  status: MarketCohortPreviewStatus;
  label: string;
  expectedSampleSize?: number;
  tradeIds: string[];
  total: number;
  error?: string;
  rowKey: string;
  listFilter: CohortListFilter;
};

/** Max concurrent Preview trades panels (exit placement / market context). */
export const MAX_MARKET_COHORT_PREVIEWS = 3;

const MAX_PREVIEWS_TOAST =
  "Maximum previews have been added. Please clear a preview to add more.";

function bucketRowKey(row: ConditionAnalysisBucketRow): string {
  return `bucket:${String(row.bucket ?? "")}:${String(row.segment_key ?? row.drill_key ?? "")}`;
}

function situationRowKey(row: ConditionAnalysisSituationRow): string {
  const situation = String(row.situation ?? "").trim();
  const sample = row.sample_size ?? "";
  return `situation:${situation}::${sample}`;
}

function explorerRowKey(sliceId: string): string {
  return `slice:${sliceId}`;
}

function sliceRowKey(sliceId: string): string {
  return `configured-slice:${sliceId}`;
}

function drilldownPreviewRowKey(tableKey: string, rowKey: string): string {
  return `drilldown:${tableKey}:${rowKey}`;
}

function scrollToPreviewPanel(rowKey?: string): void {
  requestAnimationFrame(() => {
    const selector = rowKey
      ? `[data-market-cohort-preview="${CSS.escape(rowKey)}"]`
      : "[data-market-cohort-preview]";
    document.querySelector(selector)?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function errorMessageFromUnknown(err: unknown): string {
  if (err instanceof ReportsApiError) {
    return err.message;
  }
  if (err instanceof Error && err.message.trim()) {
    return err.message;
  }
  return "Failed to resolve cohort trades.";
}

type AcquireResult = "toggle-off" | "blocked" | "added";

export function useMarketCohortPreview(
  reportId: string,
  options?: {
    resolvePage?: (
      id: string,
      body: CohortTradesResolveRequestBody,
      query?: { offset?: number; limit?: number }
    ) => Promise<import("../../../utils/cohortTradesResolve").CohortTradesResolveResponse>;
  }
) {
  const [previews, setPreviews] = useState<MarketCohortPreviewState[]>([]);
  /** Source of truth for acquire/patch — do not sync from render (concurrent renders clobber newer refs). */
  const previewsRef = useRef<MarketCohortPreviewState[]>([]);
  const requestSeqByKey = useRef(new Map<string, number>());
  const reportIdRef = useRef(reportId);
  const resolvePageRef = useRef(options?.resolvePage);
  resolvePageRef.current = options?.resolvePage;

  if (reportIdRef.current !== reportId) {
    reportIdRef.current = reportId;
    previewsRef.current = [];
    requestSeqByKey.current = new Map();
    if (previews.length > 0) {
      setPreviews([]);
    }
  }

  const bumpRequestSeq = useCallback((rowKey: string): number => {
    const next = (requestSeqByKey.current.get(rowKey) ?? 0) + 1;
    requestSeqByKey.current.set(rowKey, next);
    return next;
  }, []);

  const replacePreviews = useCallback((next: MarketCohortPreviewState[]) => {
    previewsRef.current = next;
    setPreviews(next);
  }, []);

  const clearPreview = useCallback(
    (rowKey?: string) => {
      if (!rowKey) {
        for (const key of Array.from(requestSeqByKey.current.keys())) {
          bumpRequestSeq(key);
        }
        replacePreviews([]);
        return;
      }
      bumpRequestSeq(rowKey);
      replacePreviews(previewsRef.current.filter((p) => p.rowKey !== rowKey));
    },
    [bumpRequestSeq, replacePreviews]
  );

  const setListFilter = useCallback(
    (rowKey: string, listFilter: CohortListFilter) => {
      replacePreviews(
        previewsRef.current.map((p) => (p.rowKey === rowKey ? { ...p, listFilter } : p))
      );
    },
    [replacePreviews]
  );

  const patchPreview = useCallback(
    (rowKey: string, patch: Partial<MarketCohortPreviewState>) => {
      replacePreviews(
        previewsRef.current.map((p) => (p.rowKey === rowKey ? { ...p, ...patch } : p))
      );
    },
    [replacePreviews]
  );

  /**
   * Acquire a preview slot for `rowKey`.
   * - Existing key → clear (toggle off)
   * - At max → toast + blocked
   * - Else → append loading entry
   *
   * Uses a ref so the max-cap check (and toast) runs synchronously — reading a
   * flag mutated inside a setState updater is unreliable under concurrent React.
   */
  const acquirePreviewSlot = useCallback(
    (params: {
      rowKey: string;
      label: string;
      expectedSampleSize?: number;
      initial?: Partial<Pick<MarketCohortPreviewState, "status" | "tradeIds" | "total">>;
    }): AcquireResult => {
      const prev = previewsRef.current;

      if (prev.some((p) => p.rowKey === params.rowKey)) {
        bumpRequestSeq(params.rowKey);
        replacePreviews(prev.filter((p) => p.rowKey !== params.rowKey));
        return "toggle-off";
      }

      if (prev.length >= MAX_MARKET_COHORT_PREVIEWS) {
        toast.info(MAX_PREVIEWS_TOAST);
        return "blocked";
      }

      replacePreviews([
        ...prev,
        {
          status: params.initial?.status ?? "loading",
          label: params.label,
          expectedSampleSize: params.expectedSampleSize,
          tradeIds: params.initial?.tradeIds ?? [],
          total: params.initial?.total ?? 0,
          rowKey: params.rowKey,
          listFilter: "cohort",
          error: undefined,
        },
      ]);
      return "added";
    },
    [bumpRequestSeq, replacePreviews]
  );

  const runResolve = useCallback(
    async (params: {
      rowKey: string;
      label: string;
      expectedSampleSize?: number;
      body: CohortTradesResolveRequestBody;
    }): Promise<{ tradeIds: string[]; total: number; ok: boolean } | null> => {
      const gate = acquirePreviewSlot({
        rowKey: params.rowKey,
        label: params.label,
        expectedSampleSize: params.expectedSampleSize,
      });
      if (gate !== "added") return null;

      const seq = bumpRequestSeq(params.rowKey);

      try {
        const { tradeIds, total } = await resolveAllCohortTradeIds(reportId, params.body, {
          resolvePage: resolvePageRef.current,
        });
        if (seq !== requestSeqByKey.current.get(params.rowKey)) return null;

        if (tradeIds.length === 0) {
          patchPreview(params.rowKey, {
            status: "empty",
            tradeIds: [],
            total: 0,
          });
          scrollToPreviewPanel(params.rowKey);
          return { tradeIds: [], total: 0, ok: false };
        }

        patchPreview(params.rowKey, {
          status: "ready",
          tradeIds,
          total,
        });
        scrollToPreviewPanel(params.rowKey);
        return { tradeIds, total, ok: true };
      } catch (err) {
        if (seq !== requestSeqByKey.current.get(params.rowKey)) return null;
        patchPreview(params.rowKey, {
          status: "error",
          error: errorMessageFromUnknown(err),
          tradeIds: [],
          total: 0,
        });
        scrollToPreviewPanel(params.rowKey);
        return { tradeIds: [], total: 0, ok: false };
      }
    },
    [acquirePreviewSlot, bumpRequestSeq, patchPreview, reportId]
  );

  const previewConditionBucket = useCallback(
    (
      row: ConditionAnalysisBucketRow,
      sliceContext: ReportPickSliceContext | null | undefined,
      analysisScope?: ConditionAnalysisScope | null,
      label?: string
    ) => {
      void runResolve({
        rowKey: bucketRowKey(row),
        label: label ?? String(row.bucket ?? "Bucket"),
        expectedSampleSize: row.sample_size,
        body: buildBucketRowResolveBody(row, sliceContext, analysisScope),
      });
    },
    [runResolve]
  );

  const previewConditionSituation = useCallback(
    (
      row: ConditionAnalysisSituationRow,
      sliceContext: ReportPickSliceContext | null | undefined,
      analysisScope?: ConditionAnalysisScope | null,
      label?: string
    ) => {
      void runResolve({
        rowKey: situationRowKey(row),
        label: label ?? String(row.situation ?? "Situation"),
        expectedSampleSize: row.sample_size,
        body: buildSituationRowResolveBody(row, sliceContext, analysisScope),
      });
    },
    [runResolve]
  );

  const previewExplorerSlice = useCallback(
    (row: ManagementExplorerSliceRow, label: string, expectedSampleSize?: number) => {
      const sliceId = buildExplorerSliceId(row) || label.trim();
      if (!sliceId) return;
      const rowKey = explorerRowKey(sliceId);

      const persistedIds = Array.isArray(row.trade_ids)
        ? row.trade_ids.map((id) => String(id ?? "").trim()).filter(Boolean)
        : [];

      if (persistedIds.length > 0) {
        const gate = acquirePreviewSlot({
          rowKey,
          label,
          expectedSampleSize,
          initial: {
            status: "ready",
            tradeIds: persistedIds,
            total: persistedIds.length,
          },
        });
        if (gate === "added") {
          scrollToPreviewPanel(rowKey);
        }
        return;
      }

      const body = buildExplorerRowCohortResolveBody(row);
      if (!body) return;
      void runResolve({
        rowKey,
        label,
        expectedSampleSize,
        body,
      });
    },
    [acquirePreviewSlot, runResolve]
  );

  const previewConditionSlice = useCallback(
    (
      slice: ConditionAnalysisSlice,
      _sliceContext: ReportPickSliceContext | null | undefined,
      label: string,
      expectedSampleSize?: number
    ) => {
      const body = buildConditionSliceResolveBody(slice);
      if (!body) return;
      void runResolve({
        rowKey: sliceRowKey(slice.id),
        label,
        expectedSampleSize,
        body,
      });
    },
    [runResolve]
  );

  const previewConditionSummaryRow = useCallback(
    (
      tableKey: string,
      row: ConditionAnalysisSummaryRow,
      _sliceContext: ReportPickSliceContext | null | undefined,
      label: string,
      expectedSampleSize?: number
    ) => {
      const body = buildSummaryRowResolveBody(tableKey, row);
      if (!body) return;
      void runResolve({
        rowKey: drilldownPreviewRowKey(tableKey, row.key),
        label,
        expectedSampleSize,
        body,
      });
    },
    [runResolve]
  );

  const activeRowKeys = useMemo(() => previews.map((p) => p.rowKey), [previews]);
  const previewLoadingKeys = useMemo(
    () => previews.filter((p) => p.status === "loading").map((p) => p.rowKey),
    [previews]
  );

  return {
    previews,
    /** Alias for table “Active” highlighting — array of active preview row keys. */
    activeRowKey: activeRowKeys,
    activeRowKeys,
    previewLoadingKey: previewLoadingKeys,
    previewLoadingKeys,
    clearPreview,
    setListFilter,
    previewConditionBucket,
    previewConditionSituation,
    previewConditionSlice,
    previewConditionSummaryRow,
    previewExplorerSlice,
  };
}
